<template>
  <OptimizationDrawer
    v-model:drawer-visibility="optimizationsDrawerVisibility"
    :inital-filter="drawerFilter" />

  <el-card
    shadow="always"
    class="h-full min-h-80"
    :class="props.class"
    :body-style="{
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    }">
    <div v-if="loadOptimizations" class="flex h-full flex-col gap-8 p-4">
      <div class="flex flex-col gap-4">
        <SmSkeleton height="30px" width="20rem" />
        <SmSkeleton height="20px" width="24rem" />
        <SmSkeleton height="20px" width="10rem" />
      </div>
    </div>

    <EmptyState
      v-else-if="!loadOptimizations && optimizations.length === 0"
      :icon="CircleCheck"
      :title="i18n.t('noOptimizationsTitle')"
      :text="i18n.t('noOptimizationsText')"
      :show-docs-btn="false"
      :show-create-btn="true"
      @create="refetchOptimizations()">
      <template #create-btn>
        {{ i18n.t('reloadOptimizations') }}
      </template>
    </EmptyState>

    <div v-else class="flex h-full flex-col gap-12 p-4">
      <div class="flex justify-between gap-4">
        <div class="basis-1/2">
          <h2 class="mb-1">
            {{
              i18n.t('optimizationsCount', {
                count: optimizations.length,
              })
            }}
          </h2>
          <p class="mb-4">
            <i18n-t keypath="savingsPotentialText">
              <template #savings>
                <span class="whitespace-nowrap font-bold">{{
                  getCorrectedPrice(savings * 12)
                }}</span>
              </template>
            </i18n-t>
          </p>

          <SmButton
            class="mt-auto"
            outline
            :text="i18n.t('showAllOptimizations')"
            @click="optimizationsDrawerVisibility = true" />
        </div>

        <SmSelect
          v-model:selection="selection"
          :options="options"
          :label="selection[0].label"
          trigger-style="default"
          dropdown-min-width="350px">
        </SmSelect>
      </div>

      <div
        class="flex w-full"
        :style="{ maxHeight: 'calc(100% - 100px - 3rem)' }">
        <div class="relative w-full">
          <BarChart
            ref="chartRef"
            :options="chartOptions"
            :chart-data="chartData"
            :width="1000"
            class="h-full max-h-full min-h-24 w-full" />
        </div>
      </div>
    </div>
  </el-card>
</template>

<script lang="ts" setup>
  import { OptimizationFilters } from '@/stores/optimization/optimization.filter'
  import { BarChart } from 'vue-chart-3'
  import {
    OptimizationStore,
    EnrichedOptimizations,
  } from '@/stores/optimization/optimization.store'
  import { Option } from '../sm/SmSelect.vue'
  import { useI18n } from 'vue-i18n'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { ActiveElement, ChartEvent, TooltipItem } from 'chart.js'
  import { CircleCheck } from 'lucide-vue-next'
  import { refetchOptimizations } from '@/utils/optimizations'

  const i18n = useI18n()
  const optimizationsDrawerVisibility = ref<boolean>(false)

  const props = defineProps({
    class: {
      type: String,
      required: false,
      default: '',
    },
  })

  // ----------------------------------------------------------------------
  // Load Data
  // ----------------------------------------------------------------------

  const loadOptimizations = ref(true)

  const filter = ref<OptimizationFilters>({
    hidden: false,
    deprecated: false,
  })

  const { data: optimizations } =
    OptimizationStore.useOptimizations<EnrichedOptimizations>(
      filter.value,
      undefined,
      undefined,
      true
    )

  const { savings } = OptimizationStore.useOptimizationsSavings()

  watch(optimizations, () => {
    loadOptimizations.value = false
  })

  // ----------------------------------------------------------------------
  // Selection
  // ----------------------------------------------------------------------

  const options = ref<Option<string>[]>([
    {
      label: i18n.t(`optimizationChart.application`),
      value: 'application',
    },
    {
      label: i18n.t(`optimizationChart.type`),
      value: 'type',
    },
    {
      label: i18n.t(`optimizationChart.savings`),
      value: 'savings',
    },
  ])

  const selection = ref<Option<string>[]>([options.value[0]])

  // ----------------------------------------------------------------------
  // Chart
  // ----------------------------------------------------------------------

  type ChartItem = {
    label: string
    id?: string
    count: number
  }

  const itemsbyType = computed(() => {
    return optimizations.value
      .reduce((result, optimization) => {
        const existing = result.find((i) => i.label === optimization.type)
        if (existing) {
          existing.count += 1
        } else {
          result.push({
            label: optimization.type,
            count: 1,
          })
        }
        return result
      }, [] as ChartItem[])
      .sort((a, b) => b.count - a.count)
  })

  const itemsbyApplicationId = computed(() => {
    const optimizationsWithApps = optimizations as Ref<EnrichedOptimizations[]>

    return optimizationsWithApps.value
      .reduce((result, optimization) => {
        const existing = result.find(
          (item) => item.label === optimization.application_display_name
        )
        if (existing) {
          existing.count += 1
        } else if (optimization.application_display_name) {
          result.push({
            label: optimization.application_display_name,
            id: optimization.application_id,
            count: 1,
          })
        }
        return result
      }, [] as ChartItem[])
      .sort((a, b) => b.count - a.count)
  })

  const itemsbySavings = computed(() => {
    const optimizationsWithApps = optimizations as Ref<EnrichedOptimizations[]>

    return optimizationsWithApps.value
      .reduce((result, optimization) => {
        const existing = result.find(
          (item) => item.label === optimization.application_display_name
        )

        const yearlySavings = optimization.savings * 12
        if (existing) {
          existing.count += yearlySavings || 0
        } else if (optimization.application_display_name) {
          result.push({
            label: optimization.application_display_name,
            id: optimization.application_id,
            count: yearlySavings || 0,
          })
        }
        return result
      }, [] as ChartItem[])
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count)
  })

  const source = computed(() => {
    switch (selection.value[0].value) {
      case 'application':
        return itemsbyApplicationId.value
      case 'type':
        return itemsbyType.value
      case 'savings':
        return itemsbySavings.value
      default:
        return []
    }
  })

  function getChartLabel(label: string) {
    switch (selection.value[0].value) {
      case 'application':
        return label
      case 'savings':
        return label
      case 'type':
        return i18n.t(`optimizationNames.${label}`)
    }
  }

  const chartColors = [
    '--sm-purple',
    '--sm-blue',
    '--sm-cyan',
    '--sm-green',
    '--sm-lime',
    '--sm-yellow',
    '--sm-orange',
    '--sm-red',
    '--sm-magenta',
    '--sm-lavender',
  ]

  const resolvedColors = chartColors.map((color) =>
    getComputedStyle(document.documentElement).getPropertyValue(color).trim()
  )

  const chartData = computed(() => {
    const limitedSource = source.value.slice(0, 10)

    return {
      labels: limitedSource.map((item) => getChartLabel(item.label)),
      datasets: [
        {
          data: limitedSource.map((item) => item.count),
          backgroundColor: resolvedColors,
        },
      ],
    }
  })

  const yAxisMax = ref(100)

  watch(chartData, () => {
    yAxisMax.value = Math.max(...chartData.value.datasets[0].data)
  })

  const chartOptions = ref({
    onClick: (event: Event, elements: ActiveElement[]) => {
      if (!elements.length) return
      const index = elements[0].index
      const item = source.value[index]

      handleChartClick(item)
    },
    onHover: (event: ChartEvent, elements: ActiveElement[]) => {
      const target = event.native?.target as HTMLElement
      if (target) {
        target.style.cursor = elements.length ? 'pointer' : 'default'
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        padding: 10,
        displayColors: false,
        callbacks: {
          title: function (tooltipItems: TooltipItem<'bar'>[]) {
            return tooltipItems[0].label
          },
          label: function (context: TooltipItem<'bar'>) {
            const value = context.parsed.y || 0

            switch (selection.value[0].value) {
              case 'application':
                return `${i18n.t('optimizationsCount', { count: value })}`
              case 'savings':
                return `${i18n.t('yearlySavings')}: ${getCorrectedPrice(value)}`
              case 'type':
                return `${i18n.t('optimizationsCount', { count: value })}`
              default:
                return
            }
          },
        },
      },
    },
    layout: {
      padding: -5,
    },
    elements: {
      bar: {
        borderRadius: 12,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        type: computed(() => {
          const data = chartData.value.datasets[0].data
          const min = Math.min(...data)
          const max = Math.max(...data)
          return min < 0.1 * max ? 'logarithmic' : 'linear'
        }),
        beginAtZero: true,
        grace: '5%',
        border: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 5,
          display: false,
        },
        max: () => yAxisMax.value,
      },
    },
  })

  // Handle Bar Click
  const drawerFilter = ref<OptimizationFilters>({
    hidden: false,
    deprecated: false,
    hideExpenseless: true,
  })

  function handleChartClick(item: ChartItem) {
    if (['application', 'savings'].includes(selection.value[0].value)) {
      drawerFilter.value = {
        application_ids: [item.id ?? ''],
      }
    } else if (selection.value[0].value === 'type') {
      drawerFilter.value = {
        type: [item.label],
      }
    }

    optimizationsDrawerVisibility.value = true
  }
</script>

<style scoped></style>
